import COLORS, { COLOR_VALUES } from "shared/config/colors";
import {
    DataItemCard,
    StyledButton,
    StyledDescriptionText,
    StyledLink,
    StyledLoaderWrapper,
} from "./DashboardDataItem.styles";

import HStack from "shared/components/layout/stack/HStack";
import Icon from "shared/components/utils/Icon";
import Loader from "shared/components/feedback/loading/Loader";
import PropTypes from "prop-types";
import React from "react";
import TertiaryButton from "shared/components/inputs/button/TertiaryButton";
import Text from "shared/components/data-display/text/Text";
import Tooltip from "shared/components/utils/tooltip/Tooltip";
import { getCurrencyToLocaleString } from "shared/helper-functions/format/formatCurrency";

const DashboardDataItem = ({
    title,
    placeholder = "",
    count = null,
    description,
    info = null,
    isCurrency = true,
    currencySymbol,
    isLoading = true,
    tooltipText = null,
    onClick = () => void 0,
    actionProps = {},
    isNextLine = false,
    actionIconWidth = 16,
    feedbackMessage = "",
    infoActionIcon,
    titleActionIconColor,
    iconTooltipText,
    titleActionIcon,
    linkData,
    href,
    isInternalUrlNewTab,
    feedbackIconColor,
    feedbackIcon,
}) => {
    const isNumber = typeof count === "number";
    const renderNumberData = (isNegativeVal = false) => {
        return (
            <Text
                variant={"Headline_3"}
                color={isNegativeVal ? COLORS.Orange_1 : COLORS.Black}
            >
                {isNegativeVal && <>&#40;</>}
                {isCurrency && currencySymbol}
                {getCurrencyToLocaleString(count)}
                {!isCurrency && <>&#37;</>}
                {isNegativeVal && <>&#41;</>}
            </Text>
        );
    };
    const renderContent = () => {
        return (
            <>
                {isNumber && count < 0
                    ? renderNumberData(true)
                    : isNumber && renderNumberData(false)}
                {info && (
                    <HStack spacing="Zero_25">
                        <Text variant={"Body_2_1"} color={COLORS.Black}>
                            {info}
                        </Text>
                        {infoActionIcon && (
                            <TertiaryButton
                                {...actionProps}
                                trailingIcon={infoActionIcon}
                                iconWidth={actionIconWidth}
                                style={{
                                    color: "currentColor",
                                    background: "transparent",
                                }}
                            />
                        )}
                    </HStack>
                )}
                {href ? (
                    <StyledLink
                        href={href}
                        isInternalUrlNewTab={isInternalUrlNewTab}
                    >
                        <Text variant="Headline_3" color={COLORS.Black}>
                            {linkData}
                        </Text>
                    </StyledLink>
                ) : (
                    <Text variant="Headline_3" color={COLORS.Black}>
                        {linkData}
                    </Text>
                )}
                {!isNumber && !info && (
                    <Text
                        variant={isNumber ? "Headline_3" : "Body_2_1"}
                        color={COLORS.Black}
                    >
                        {placeholder}
                    </Text>
                )}
                <StyledDescriptionText
                    variant={"Descriptive_2_1"}
                    color={COLORS.Gray_4}
                    isNextLine={isNextLine}
                >
                    {description}
                </StyledDescriptionText>
                {feedbackMessage && (
                    <HStack spacing="Zero_25" align="center">
                        <Icon name={feedbackIcon} color={feedbackIconColor} />
                        <Text color="Black" variant="Body_2_2">
                            {feedbackMessage}
                        </Text>
                    </HStack>
                )}
            </>
        );
    };
    return (
        <Tooltip text={tooltipText} enabled={tooltipText} enterDelay={0}>
            <DataItemCard>
                <HStack spacing="Zero_25">
                    <Text variant={"Descriptive_1_1"} color={COLORS.Gray_2}>
                        {title}
                    </Text>
                    {titleActionIcon && (
                        <Tooltip
                            text={iconTooltipText}
                            enabled={iconTooltipText}
                            enterDelay={0}
                        >
                            <StyledButton type="button" onClick={onClick}>
                                <Icon
                                    name={titleActionIcon}
                                    color={titleActionIconColor}
                                />
                            </StyledButton>
                        </Tooltip>
                    )}
                </HStack>
                {isLoading ? (
                    <StyledLoaderWrapper>
                        <Loader
                            width="18px"
                            height="18px"
                            color={COLOR_VALUES.Gray_1}
                        />
                    </StyledLoaderWrapper>
                ) : (
                    renderContent()
                )}
            </DataItemCard>
        </Tooltip>
    );
};

DashboardDataItem.propTypes = {
    title: PropTypes.string,
    placeholder: PropTypes.string,
    count: PropTypes.number,
    description: PropTypes.string,
    info: PropTypes.string,
    isCurrency: PropTypes.bool,
    currencySymbol: PropTypes.string,
    isLoading: PropTypes.bool,
    tooltipText: PropTypes.string,
    onClick: PropTypes.func,
    actionProps: PropTypes.object,
    isNextLine: PropTypes.bool,
    actionIconWidth: PropTypes.number,
    feedbackMessage: PropTypes.string,
    infoActionIcon: PropTypes.string,
    titleActionIconColor: PropTypes.string,
    iconTooltipText: PropTypes.string,
    titleActionIcon: PropTypes.string,
    linkData: PropTypes.object,
    href: PropTypes.string,
    isInternalUrlNewTab: PropTypes.bool,
    feedbackIconColor: PropTypes.string,
    feedbackIcon: PropTypes.string,
};

export default DashboardDataItem;
